import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, fmtDate, panelPost, useAdminGet,
} from '../kit';
import { AdminLink } from '../adminNav';
import { Check, DateRange, Labeled, inputCls } from '../email/emailKit';
import { fmtIST } from '../email/trace/DeliveryTrace';

/**
 * Leads & Marketing → Contact University / Talk to Concierge (owner,
 * 2026-10-08: "individual options needed, individual tables/filters to view
 * it"). One component for both kinds: the team e-mail card (on/off,
 * recipients, a daily cap per person, skip our own team, a test send) over a
 * server-filtered, server-paged list of every opening / click.
 * Backend: cta_alerts.py — leads/cta_settings, cta_settings_set,
 * cta_test_email and cta_events.
 */

export type CtaKind = 'contact_university' | 'talk_to_concierge';

type Flash = { kind: 'error' | 'ok'; text: string } | null;

const KIND: Record<CtaKind, {
  title: string; noun: string; nouns: string; kpi: string; check: string;
  templateName: string; csv: string; explain: string; history: string;
}> = {
  contact_university: {
    title: 'Contact University',
    noun: 'opening',
    nouns: 'openings',
    kpi: 'Opens',
    check: 'E-mail the team on every opening',
    templateName: 'Contact University opened (team alert)',
    csv: 'contact_university_opens',
    explain: 'Each e-mail says who opened it (name, e-mail, organisation, role, a link to their account), '
      + 'their plan and how many university contacts they have used; the technology, its university and '
      + 'public page; where on the site they opened it and on which page; the result — contacts shown '
      + '(and which contacts they saw), shown again, nothing to show, or blocked by their limit or plan; '
      + 'and how often they opened this technology before and how many they opened today. Reply goes '
      + 'straight to the person. Fetching contacts to send an in-app e-mail is listed below but never '
      + 'e-mailed.',
    history: 'openings from before per-open logging started',
  },
  talk_to_concierge: {
    title: 'Talk to Concierge',
    noun: 'click',
    nouns: 'clicks',
    kpi: 'Clicks',
    check: 'E-mail the team on every click',
    templateName: 'Talk to Concierge clicked (team alert)',
    csv: 'talk_to_concierge_clicks',
    explain: 'Each e-mail says who clicked (name, e-mail, organisation, role, a link to their account), '
      + 'their plan and how many university contacts they have used; the technology, its university and '
      + 'public page; where on the site the Contact University sheet was opened and on which page; and '
      + 'how many times they clicked Talk to Concierge before. They were taken to the booking calendar, '
      + 'so check it for a booking. Reply goes straight to the person.',
    history: 'clicks from before this list recorded the details',
  },
};

// ---------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------
const Pill: React.FC<{ tone: 'ok' | 'warn' | 'bad' | 'mute'; children: React.ReactNode }> =
  ({ tone, children }) => {
  const look = tone === 'ok' ? 'text-green-700 bg-green-50 border-green-200'
    : tone === 'warn' ? 'text-amber-700 bg-amber-50 border-amber-200'
      : tone === 'bad' ? 'text-red-600 bg-red-50 border-red-200'
        : 'text-grayscale-1200 bg-grayscale-200 border-grayscale-200';
  return <span className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${look}`}>{children}</span>;
};

const OUTCOME_TONE: Record<string, 'ok' | 'bad' | 'mute'> = {
  shown: 'ok', already: 'ok', empty: 'mute', before_log: 'mute', limit: 'bad', upgrade: 'bad',
};
const EMAIL_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'mute'> = {
  sent: 'ok', sending: 'warn', capped: 'warn', failed: 'bad',
};

/** Keeps the ILIKE searches off every keystroke. */
function useDebounced(value: string, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// ---------------------------------------------------------------------------
// Team e-mail card
// ---------------------------------------------------------------------------
type Draft = { enabled: boolean; recipients: string; cap: string; skipTeam: boolean };
const draftFrom = (k: any): Draft => ({
  enabled: !!k?.enabled,
  recipients: (Array.isArray(k?.recipients) ? k.recipients : []).join(', '),
  cap: String(k?.daily_cap ?? 0),
  skipTeam: k?.skip_team !== false,
});

const AlertCard: React.FC<{ kind: CtaKind }> = ({ kind }) => {
  const meta = KIND[kind];
  const cfg = useAdminGet('leads', 'cta_settings');
  const saved = cfg.data?.kinds?.[kind];
  const inbox = String(cfg.data?.default_inbox || 'info@universitytech.io');
  const maxCap = Number(cfg.data?.max_cap || 1000);
  const [msg, setMsg] = useState<Flash>(null);
  const [testing, setTesting] = useState(false);

  // Same draft handling as Users → Contact limits: `dirty` keeps a half-typed
  // edit from being overwritten by a refetch; `seededFrom` makes a save's own
  // answer stick until the refetch brings the same values back.
  const [draft, setDraft] = useState<Draft>(draftFrom(null));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const seededFrom = useRef<any>(null);
  useEffect(() => {
    if (!saved || dirty || seededFrom.current === saved) return;
    seededFrom.current = saved;
    setDraft(draftFrom(saved));
  }, [saved, dirty]);
  const edit = (patch: Partial<Draft>) => { setDraft(d => ({ ...d, ...patch })); setDirty(true); };
  const discard = () => { if (saved) setDraft(draftFrom(saved)); seededFrom.current = saved; setDirty(false); };

  const status = (k: any): string => {
    if (!k?.enabled) return `off — ${meta.nouns} are still listed below, nobody is e-mailed.`;
    const cap = Number(k.daily_cap || 0);
    return `on — to ${(k.effective_recipients || []).join(', ') || inbox}, `
      + `${cap ? `at most ${cap} per person a day` : 'no daily limit per person'}`
      + `${k.skip_team ? ', never for our own team' : ''}.`;
  };

  const save = async () => {
    const raw = draft.cap.trim();
    const cap = raw === '' ? 0 : Number(raw);
    if (!Number.isInteger(cap) || cap < 0 || cap > maxCap) {
      setMsg({ kind: 'error', text: `The daily cap must be a whole number from 0 to ${maxCap} (0 = no limit).` });
      return;
    }
    setSaving(true);
    const { data, error } = await panelPost('leads', 'cta_settings_set', {
      kind, enabled: draft.enabled, recipients: draft.recipients, daily_cap: cap, skip_team: draft.skipTeam,
    });
    setSaving(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    const k = data?.kinds?.[kind];
    if (k) setDraft(draftFrom(k));
    seededFrom.current = saved;
    setDirty(false);
    setMsg({ kind: 'ok', text: k ? `Saved — ${status(k)}` : 'Saved.' });
    cfg.reload();
  };

  const test = async () => {
    setTesting(true);
    const { data, error } = await panelPost('leads', 'cta_test_email', { kind });
    setTesting(false);
    if (error || data?.success === false) {
      setMsg({ kind: 'error', text: `Test not sent: ${error || data?.error || 'unknown error'}` });
      return;
    }
    setMsg({ kind: 'ok', text: `Test sent to ${data?.sent_to || 'you'} — built from `
      + (data?.from_click ? `the newest real ${meta.noun}.` : `the template's example values (no real ${meta.noun} yet).`) });
  };

  return (
    <Section title={`Team e-mail on a ${meta.title} ${meta.noun}`}
      right={<Btn small onClick={cfg.reload}>Refresh</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">{meta.explain}</p>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {cfg.loading && !saved ? <Loading /> : !saved ? (cfg.error ? <Msg kind="error">{cfg.error}</Msg> : null) : (
        <>
          <div className="flex flex-col gap-2">
            <Check checked={draft.enabled} onChange={v => edit({ enabled: v })}>{meta.check}</Check>
            <Labeled label="Send to" hint={`comma-separated · leave empty for ${inbox}`}>
              <input value={draft.recipients} onChange={e => edit({ recipients: e.target.value })}
                placeholder={inbox} spellCheck={false} className={inputCls} />
            </Labeled>
            <label className="flex items-center gap-2 text-sm text-grayscale-1200 flex-wrap">
              At most
              <input type="number" min={0} max={maxCap} step={1} value={draft.cap}
                aria-label="E-mails per person per day"
                onChange={e => edit({ cap: e.target.value })}
                className="h-9 w-24 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
              e-mails per person per day <span className="text-xs text-grayscale-1100">(0 = no limit; days in IST)</span>
            </label>
            <Check checked={draft.skipTeam} onChange={v => edit({ skipTeam: v })}>
              Don't e-mail our own team's {meta.nouns}
            </Check>
          </div>
          <div className="text-[11px] text-grayscale-1100">Right now: {status(saved)}</div>
          <div className="flex items-center gap-2 flex-wrap">
            <Btn kind="primary" onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save'}</Btn>
            {dirty && !saving && <Btn onClick={discard}>Discard changes</Btn>}
            <Btn onClick={test} disabled={testing}
              title="Sends this alert to your own address — works while it is switched off">
              {testing ? 'Sending…' : 'Send a test to me'}
            </Btn>
            <AdminLink to={{ group: 'email', tab: 'templates', template: String(saved.template_key || '') }}
              title={`Email Center → Templates (editable) → “${meta.templateName}”`}>
              Edit the e-mail
            </AdminLink>
          </div>
          <div className="text-[11px] text-grayscale-1100">
            The test is built from the newest real {meta.noun} (or the template's examples when there is none).
            Edit the e-mail opens Email Center → Templates (editable); pick “{meta.templateName}” under Team alerts.
          </div>
        </>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Filters + list
// ---------------------------------------------------------------------------
type Filters = {
  preset: string; from: string; to: string; q: string; tech: string; university: string;
  surface: string; outcome: string; action: string; tier: string; email_status: string;
  team: boolean; user_id: string;
};
const DEFAULT_FILTERS: Filters = {
  preset: '30d', from: '', to: '', q: '', tech: '', university: '', surface: '', outcome: '',
  action: '', tier: '', email_status: '', team: false, user_id: '',
};
const PRESETS: [key: string, label: string, days: number | null][] = [
  ['today', 'Today', 1], ['7d', 'Last 7 days', 7], ['30d', 'Last 30 days', 30],
  ['90d', 'Last 90 days', 90], ['all', 'All time', null],
];
const PAGE = 200;

// The server cuts days in IST, so the presets count IST days too.
const istToday = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
const daysBefore = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
};
/** The from / to a filter set asks for ({} = all time). */
const rangeOf = (f: Filters): { from?: string; to?: string } => {
  if (f.preset === 'custom') return { ...(f.from ? { from: f.from } : {}), ...(f.to ? { to: f.to } : {}) };
  const days = PRESETS.find(p => p[0] === f.preset)?.[2];
  if (!days) return {};
  const to = istToday();
  return { from: daysBefore(to, days - 1), to };
};

/** A facet as Select options, most frequent first, with counts; the current
 *  value stays listed even when the period has none of it. */
const facetOptions = (facet: Record<string, number> | undefined, labels: Record<string, string> | undefined,
                      current: string, any: string) => {
  const counts = facet || {};
  const keys = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  if (current && !keys.includes(current)) keys.push(current);
  return {
    options: ['', ...keys],
    labels: {
      '': any,
      ...Object.fromEntries(keys.map(k => [k, `${labels?.[k] ?? k} (${(counts[k] ?? 0).toLocaleString()})`])),
    },
  };
};

const CtaTab: React.FC<{ kind: CtaKind; focus?: { user: string; n: string } | null }> = ({ kind, focus }) => {
  const meta = KIND[kind];
  const isCU = kind === 'contact_university';
  // A deep link with &user= shows that person's whole history.
  const [f, setF] = useState<Filters>(() => (focus?.user
    ? { ...DEFAULT_FILTERS, preset: 'all', user_id: focus.user } : DEFAULT_FILTERS));
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    // A new link: that person's history, or — a link (or tab click) naming
    // nobody after one that did — the plain list again, not the old person.
    setF(focus?.user ? { ...DEFAULT_FILTERS, preset: 'all', user_id: focus.user } : DEFAULT_FILTERS);
    setOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.n]);
  // Every filter change starts again from the first page.
  const set = (patch: Partial<Filters>) => { setF(x => ({ ...x, ...patch })); setOffset(0); };

  const q = useDebounced(f.q.trim());
  const tech = useDebounced(f.tech.trim());
  const range = rangeOf(f);
  const params: Record<string, string | number> = { kind, limit: PAGE, offset, ...range };
  if (q) params.q = q;
  if (tech) params.tech = tech;
  if (f.university) params.university = f.university;
  if (f.surface) params.surface = f.surface;
  if (f.tier) params.tier = f.tier;
  if (f.email_status) params.email_status = f.email_status;
  if (f.user_id) params.user_id = f.user_id;
  if (f.team) params.team = 'all';
  if (isCU && f.outcome) params.outcome = f.outcome;
  if (isCU && f.action) params.action = f.action;
  const ev = useAdminGet('leads', 'cta_events', params, [JSON.stringify(params)]);
  const d: any = ev.data;
  const labels: Record<string, Record<string, string>> = d?.labels || {};
  const facets: any = d?.facets || {};

  const active = [
    f.preset !== DEFAULT_FILTERS.preset, !!f.q.trim(), !!f.tech.trim(), !!f.university, !!f.surface,
    isCU && !!f.outcome, isCU && !!f.action, !!f.tier, !!f.email_status, f.team, !!f.user_id,
  ].filter(Boolean).length;
  const badRange = f.preset === 'custom' && !!f.from && !!f.to && f.from > f.to;

  const uniOpts = useMemo(() => {
    const list: [string, number][] = facets.university || [];
    const names = list.map(x => x[0]);
    if (f.university && !names.includes(f.university)) names.push(f.university);
    const count = Object.fromEntries(list);
    return {
      options: ['', ...names],
      labels: { '': 'Any university', ...Object.fromEntries(names.map(n => [n, `${n} (${(count[n] ?? 0).toLocaleString()})`])) },
    };
  }, [facets.university, f.university]);
  const surfOpts = facetOptions(facets.surface, labels.surface, f.surface, 'Anywhere');
  const outOpts = facetOptions(facets.outcome, labels.outcome, f.outcome, 'Any result');
  const actOpts = facetOptions(facets.action, labels.action, f.action, 'Opened or sent');
  const tierOpts = facetOptions(facets.tier, labels.tier, f.tier, 'Any plan');
  const mailOpts = facetOptions(facets.email_status, labels.email_status, f.email_status, 'Any e-mail status');

  const tierLabels = labels.tier || {};
  const rows = useMemo(() => (d?.rows || []).map((r: Record<string, any>) => ({
    ...r,
    // Flat text for the CSV and the row filter; the cells render richer.
    person: [r.name, r.email !== r.name ? r.email : '', r.organisation].filter(Boolean).join(' · '),
    tech: r.tech_title || r.tech_id || '',
    where: r.source === 'history' && !r.surface ? 'Not recorded (history)' : r.surface_label,
    result: r.outcome_label,
    plan: tierLabels[r.tier || 'unknown'] || r.tier || '—',
    emailed: r.email_status_label + (r.email_error ? ` — ${r.email_error}` : ''),
  })), [d]); // eslint-disable-line react-hooks/exhaustive-deps

  const k = d?.kpis || {};
  const total = Number(d?.total || 0);
  const first = total && rows.length ? offset + 1 : 0;
  const last = offset + rows.length;
  // Sorting a single page of a paged list would rank it above rows on other
  // pages that beat it, so headers only sort while everything fits on one page.
  const paged = total > rows.length;
  const columns = ['at', 'person', 'tech', 'university', 'where', ...(isCU ? ['result'] : []), 'plan', 'emailed'];
  const optional = [...(isCU ? ['action_label'] : []), 'page_url', 'tech_id', 'country', 'designation', 'user_id',
    'source', 'page_path', 'email_error'];
  const linkedName = f.user_id
    ? (() => { const r = rows.find((x: any) => x.user_id === f.user_id); return r ? (r.name || r.email) : f.user_id; })()
    : '';

  return (
    <div className="flex flex-col gap-4">
      <AlertCard kind={kind} />

      <Section title={`Filter ${meta.nouns}`}
        right={
          <div className="flex items-center gap-2">
            {active > 0 && (
              <Btn small onClick={() => { setF(DEFAULT_FILTERS); setOffset(0); }}>
                Clear {active} filter{active === 1 ? '' : 's'}
              </Btn>
            )}
            <Btn small onClick={ev.reload}>Refresh</Btn>
          </div>}>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1 text-xs text-grayscale-1100">
            <span>Period</span>
            <div className="flex gap-1 flex-wrap">
              {PRESETS.map(([key, label]) => (
                <Btn key={key} small kind={f.preset === key ? 'primary' : 'subtle'}
                  onClick={() => set({ preset: key, from: '', to: '' })}>{label}</Btn>
              ))}
            </div>
          </div>
          <DateRange label={f.preset === 'custom' ? 'Custom (IST days, both included)' : 'or pick dates (IST)'}
            from={range.from || ''} to={range.to || ''}
            onChange={(a, b) => set(a || b ? { preset: 'custom', from: a, to: b } : { preset: 'all', from: '', to: '' })} />
        </div>
        {badRange && <div className="text-[11px] text-red-700">The start date is after the end date.</div>}
        <div className="flex items-end gap-3 flex-wrap">
          <Labeled label="Person" hint="name, e-mail or organisation">
            <TextInput value={f.q} onChange={v => set({ q: v })} placeholder="Search people…" />
          </Labeled>
          <Labeled label="Technology" hint="title or id">
            <TextInput value={f.tech} onChange={v => set({ tech: v })} placeholder="Search technologies…" />
          </Labeled>
          <Labeled label="University">
            <Select value={f.university} onChange={v => set({ university: v })} {...uniOpts} />
          </Labeled>
          <Labeled label="Where">
            <Select value={f.surface} onChange={v => set({ surface: v })} {...surfOpts} />
          </Labeled>
          {isCU && (
            <Labeled label="Result">
              <Select value={f.outcome} onChange={v => set({ outcome: v })} {...outOpts} />
            </Labeled>
          )}
          {isCU && (
            <Labeled label="What">
              <Select value={f.action} onChange={v => set({ action: v })} {...actOpts} />
            </Labeled>
          )}
          <Labeled label="Plan">
            <Select value={f.tier} onChange={v => set({ tier: v })} {...tierOpts} />
          </Labeled>
          <Labeled label="E-mail">
            <Select value={f.email_status} onChange={v => set({ email_status: v })} {...mailOpts} />
          </Labeled>
          <div className="h-9 flex items-center">
            <Check checked={f.team} onChange={v => set({ team: v })}>Show our own team</Check>
          </div>
        </div>
        {f.user_id && (
          <div className="flex items-center gap-2 flex-wrap">
            <Chip label="Only" value={linkedName} onClear={() => set({ user_id: '' })} />
            <AdminLink to={{ group: 'users', tab: 'directory', user: f.user_id }}>Open their account</AdminLink>
          </div>
        )}
      </Section>

      {ev.error && <Msg kind="error">{ev.error}</Msg>}
      {d && (
        <KPIRow>
          <KPI label={meta.kpi} value={Number(k.events || 0).toLocaleString()} />
          <KPI label="People" value={Number(k.people || 0).toLocaleString()} />
          <KPI label="Technologies" value={Number(k.techs || 0).toLocaleString()} />
          <KPI label="Universities" value={Number(k.universities || 0).toLocaleString()} />
          <KPI label="E-mailed" value={Number(k.emailed || 0).toLocaleString()} hint="team alerts sent"
            onClick={() => set({ email_status: f.email_status === 'sent' ? '' : 'sent' })}
            active={f.email_status === 'sent'} />
        </KPIRow>
      )}

      <Section title={`${meta.title} — every ${meta.noun}, newest first`}
        right={total > 0 ? (
          <div className="flex items-center gap-2 text-xs text-grayscale-1100">
            <span>Showing {first.toLocaleString()}–{last.toLocaleString()} of {total.toLocaleString()}</span>
            <Btn small disabled={offset === 0 || ev.loading} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Prev</Btn>
            <Btn small disabled={last >= total || ev.loading} onClick={() => setOffset(offset + PAGE)}>Next</Btn>
          </div>
        ) : undefined}>
        {d && (
          <div className="text-[11px] text-grayscale-1100 -mt-1">
            History rows (Source "history") are {meta.history}
            {isCU && d.log_started ? ` on ${fmtDate(d.log_started)}` : ''}: they have no where,{' '}
            {isCU ? 'result or e-mail' : 'or e-mail'} recorded, and Where shows "Not recorded (history)".
            Times are IST.{paged ? ' Download CSV saves this page only.' : ''}
          </div>
        )}
        {ev.loading && !d ? <Loading /> : !d ? null : (
          <DataTable rows={rows} tableId={`cta_events_${kind}`} csvName={meta.csv}
            columns={columns} optionalColumns={optional}
            {...(paged ? { onSortChange: () => {}, serverSort: null, unsortable: [...columns, ...optional] } : {})}
            labels={{
              at: 'When (IST)', person: 'Person', tech: 'Technology', university: 'University', where: 'Where',
              result: 'Result', plan: 'Plan', emailed: 'E-mail', action_label: 'What', page_url: 'Page',
              tech_id: 'Tech id', country: 'Country', designation: 'Role', user_id: 'User id', source: 'Source',
              page_path: 'Public page', email_error: 'E-mail error',
            }}
            empty={active ? `No ${meta.nouns} match these filters.` : `No ${meta.nouns} in this period.`}
            render={{
              at: r => <span title={r.at}>{fmtIST(r.at)}</span>,
              person: r => (
                <span className="flex items-start gap-1.5">
                  <span className="flex flex-col leading-tight min-w-0">
                    {r.user_id ? (
                      <AdminLink to={{ group: 'users', tab: 'directory', user: String(r.user_id) }}
                        title="Open this account in Users → Directory"
                        className="text-primary-700 hover:underline font-medium truncate">
                        {r.name || r.email || r.user_id}
                      </AdminLink>
                    ) : <span className="font-medium truncate">{r.name || r.email || '—'}</span>}
                    {r.email && r.email !== r.name && (
                      <span className="text-[11px] text-grayscale-1100 truncate">{r.email}</span>
                    )}
                    {r.organisation && (
                      <span className="text-[11px] text-grayscale-1100 truncate">{r.organisation}</span>
                    )}
                  </span>
                  {r.is_team && <Pill tone="mute">team</Pill>}
                  {r.user_id && f.user_id !== r.user_id && (
                    <button type="button" onClick={() => set({ user_id: String(r.user_id) })}
                      title={`Show only this person's ${meta.nouns}`}
                      className="text-[11px] text-grayscale-1100 underline underline-offset-2">only</button>
                  )}
                </span>
              ),
              tech: r => !r.tech_id ? <span className="text-grayscale-1100">{r.tech || '—'}</span> : (
                <span className="flex flex-col leading-tight min-w-0">
                  <AdminLink to={{ group: 'tech_pages', tab: 'tech', target: String(r.tech_id) }}
                    title={`${r.tech || r.tech_id} — open in Tech pages`}
                    className="text-primary-700 hover:underline truncate">
                    {r.tech || r.tech_id}
                  </AdminLink>
                  {r.page_path && (
                    <a href={r.page_path} target="_blank" rel="noreferrer"
                      className="text-[11px] text-grayscale-1100 hover:underline">public page ↗</a>
                  )}
                </span>
              ),
              where: r => r.surface
                ? <span title={r.page_url || undefined}>{r.where}</span>
                : <span className="text-grayscale-1100">{r.where}</span>,
              result: r => r.outcome
                ? <Pill tone={OUTCOME_TONE[r.outcome] || 'mute'}>{r.outcome_label}</Pill>
                : <span className="text-grayscale-1100">—</span>,
              emailed: r => (r.action === 'send' && !r.email_status
                // A fetch to send an in-app e-mail never sends a team alert.
                ? <span className="text-grayscale-1100" title="Fetching contacts to send an in-app e-mail is never e-mailed to the team">not alerted</span>
                : (
                  <span title={r.email_error || undefined}>
                    <Pill tone={EMAIL_TONE[r.email_status || ''] || 'mute'}>{r.email_status_label || '—'}</Pill>
                  </span>
                )),
            }} />
        )}
      </Section>
    </div>
  );
};

export default CtaTab;
